import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { QueueOverview } from "../../types/queue-types";

type QueueListProps = {
  readonly queues: QueueOverview[];
  readonly loading: boolean;
  readonly onSelectQueue: (queueName: string) => void;
  readonly onRefresh: () => void;
};

const QueueList = ({
  queues,
  loading,
  onSelectQueue,
  onRefresh,
}: QueueListProps) => {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">All queues ({queues.length})</Typography>
        <Tooltip title="Refresh">
          <IconButton disabled={loading} onClick={onRefresh}>
            {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Queue name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Waiting</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Completed</TableCell>
              <TableCell align="center">Failed</TableCell>
              <TableCell align="center">Delayed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queues.map((queue) => (
              <TableRow
                key={queue.name}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  onSelectQueue(queue.name);
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {queue.name}
                    {queue.isPaused && (
                      <Chip color="warning" label="Paused" size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    color={queue.type === "bullmq" ? "primary" : "secondary"}
                    label={queue.type.toUpperCase()}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">{queue.counts.waiting}</TableCell>
                <TableCell align="center">{queue.counts.active}</TableCell>
                <TableCell align="center">{queue.counts.completed}</TableCell>
                <TableCell align="center">
                  {queue.counts.failed > 0 ? (
                    <Chip
                      color="error"
                      label={queue.counts.failed}
                      size="small"
                    />
                  ) : (
                    0
                  )}
                </TableCell>
                <TableCell align="center">{queue.counts.delayed}</TableCell>
              </TableRow>
            ))}
            {queues.length === 0 && !loading && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  <Typography color="text.secondary">
                    No queues found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default QueueList;
